import "./NewTable.scss"

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable"
import { Checkbox, SvgIcon } from "@skylead/component-library"
import React, { useEffect, useState } from "react"

import { useGlobalTranslation } from "../../../utils/hook"
import Button, { ButtonVariant } from "../../atoms/Button"
import SortableHeader from "../NewTableSortableHeader"

// Helper function to get nested property value
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : ""
  }, obj)
}

const BasicTable = ({
  columns = [],
  data = [],
  showAddColumn = false,
  onAddColumn,
  showCheckbox = true,
  selectedRows: externalSelectedRows = [],
  onSelectionChange,
  onColumnsReorder,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  const { tt } = useGlobalTranslation()

  const [internalSelectedRows, setInternalSelectedRows] = useState([])
  const [internalColumns, setInternalColumns] = useState(columns)

  const selectedRows =
    externalSelectedRows.length > 0 || onSelectionChange
      ? externalSelectedRows
      : internalSelectedRows

  const stickyColumns = internalColumns.filter(col => col.sticky && col.key !== "menu")
  const draggableColumns = internalColumns.filter(col => !col.sticky && col.key !== "menu")
  const menuColumn = internalColumns.find(col => col.key === "menu")

  const isMenuColumnSticky = menuColumn?.sticky

  const getStickyLeftOffset = columnIndex => {
    let offset = showCheckbox ? 48 : 0
    for (let i = 0; i < columnIndex; i++) {
      const { width } = stickyColumns[i]
      offset += width ? parseInt(width, 10) : 150
    }
    return offset
  }

  useEffect(() => {
    setInternalColumns(columns)
  }, [columns])

  useEffect(() => {
    if (onSelectionChange) {
      setInternalSelectedRows(externalSelectedRows)
    }
  }, [externalSelectedRows, onSelectionChange])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleSelectAll = () => {
    const allIds = data.map(item => item.id)
    const isAllSelected = selectedRows.length === data.length && data.length > 0
    const newSelection = isAllSelected ? [] : allIds

    if (onSelectionChange) {
      onSelectionChange(newSelection)
    } else {
      setInternalSelectedRows(newSelection)
    }
  }

  const handleSelectRow = item => {
    const isSelected = selectedRows.includes(item.id)
    const newSelection = isSelected
      ? selectedRows.filter(id => id !== item.id)
      : [...selectedRows, item.id]
    if (onSelectionChange) {
      onSelectionChange(newSelection)
    } else {
      setInternalSelectedRows(newSelection)
    }
  }

  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = draggableColumns.findIndex(column => column.key === active.id)
      const newIndex = draggableColumns.findIndex(column => column.key === over.id)

      const newDraggableColumns = arrayMove(draggableColumns, oldIndex, newIndex)
      const newColumns = [
        ...stickyColumns,
        ...newDraggableColumns,
        ...(menuColumn ? [menuColumn] : []),
      ]
      setInternalColumns(newColumns)

      if (onColumnsReorder) {
        onColumnsReorder(newColumns)
      }
    }
  }

  const handleSort = column => {
    if (!column.sortable || !onSort) return

    const sortLabel = column.sortable.label
    if (sortColumn === sortLabel) {
      onSort(sortLabel, sortDirection === "ASC" ? "DESC" : "ASC")
    } else {
      onSort(sortLabel, "ASC")
    }
  }

  const getSortDirection = column => {
    if (!column.sortable) return null
    return sortColumn === column.sortable.label ? sortDirection : null
  }

  const renderCellContent = (column, row) => {
    if (column.render && typeof column.render === "function") {
      return column.render(row)
    }
    return getNestedValue(row, column.key)
  }

  const renderSortableHeader = (column, isSticky = false, stickyIndex = null) => {
    const sortDir = getSortDirection(column)
    const headerStyle = isSticky
      ? {
          left: `${getStickyLeftOffset(stickyIndex)}px`,
          width: column.width || "auto",
          minWidth: column.width || "auto",
          maxWidth: column.width || "none",
          position: "sticky",
          top: 0,
          zIndex: 25,
        }
      : {
          width: column.width || "auto",
          minWidth: column.width || "auto",
          maxWidth: column.width || "none",
        }

    return (
      <th
        key={column.key}
        style={headerStyle}
        className={`small-bold ${column.className || ""} ${
          isSticky ? "sticky-header-column" : ""
        } ${column.sortable ? "sortable-header" : ""}`}
        onClick={() => column.sortable && handleSort(column)}
      >
        <>
          {column.header}
          {column.sortable && (
            <SvgIcon
              icon="arrow-narrow-down-16"
              className={`sort-item-icon ${sortDir ? "active" : ""} ${
                sortDir === "DESC" ? "fliped" : ""
              }`}
            />
          )}
        </>
      </th>
    )
  }

  const renderCheckboxColumnHeader = () => {
    return (
      <th className="checkbox-column sticky-header-checkbox">
        <Checkbox
          checked={selectedRows.length > 0}
          icon={selectedRows.length === data.length ? "checked" : "indeterminated"}
          onClick={handleSelectAll}
          label={tt("select-all")}
        />
      </th>
    )
  }

  const renderMenuColumnHeader = () => {
    return (
      <th
        key={menuColumn.key}
        style={{
          width: menuColumn.width || "auto",
          minWidth: menuColumn.width || "auto",
          maxWidth: menuColumn.width || "none",
        }}
        className={`small-bold ${menuColumn.className || ""} ${
          isMenuColumnSticky ? "sticky-menu-header" : ""
        }`}
      >
        {menuColumn.header}
      </th>
    )
  }

  const renderAddColumnHeader = () => {
    return (
      <th className="add-column-header">
        <Button
          variant={ButtonVariant.GHOST}
          icon="plus-20"
          onClick={() => {
            if (onAddColumn) {
              onAddColumn()
            } else {
              // eslint-disable-next-line no-console
              console.log("Add column clicked")
            }
          }}
          className="add-column-button-style"
        >
          {tt("Add column")}
        </Button>
      </th>
    )
  }

  const renderCheckboxColumnData = (row, rowIndex) => {
    return (
      <td
        className={`checkbox-column sticky-body-checkbox ${
          rowIndex % 2 === 0 ? "row-even-bg" : "row-odd-bg"
        }`}
      >
        <Checkbox checked={selectedRows.includes(row.id)} onClick={() => handleSelectRow(row)} />
      </td>
    )
  }

  const renderMenuColumnData = (row, rowIndex) => {
    return (
      <td
        key={`${row.id || rowIndex}-${menuColumn.key}`}
        style={{
          width: menuColumn.width || "auto",
          minWidth: menuColumn.width || "auto",
          maxWidth: menuColumn.width || "none",
        }}
        className={`${menuColumn.className || ""} ${
          isMenuColumnSticky
            ? `sticky-menu-body ${rowIndex % 2 === 0 ? "row-even-bg" : "row-odd-bg"}`
            : ""
        }`}
      >
        {renderCellContent(menuColumn, row)}
      </td>
    )
  }

  const renderAddColumnData = () => {
    return <td className="add-column-body" />
  }

  return (
    <div className="basic-table-container">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <table className="basic-table">
          <thead className="basic-table-header">
            <tr>
              {showCheckbox && renderCheckboxColumnHeader()}
              {stickyColumns.map((column, index) => renderSortableHeader(column, true, index))}

              <SortableContext
                items={draggableColumns.map(col => col.key)}
                strategy={horizontalListSortingStrategy}
              >
                {draggableColumns.map(column => (
                  <SortableHeader
                    key={column.key}
                    column={column}
                    sort={getSortDirection(column)}
                    onSort={handleSort}
                  >
                    {column.header}
                  </SortableHeader>
                ))}
              </SortableContext>
              {menuColumn && renderMenuColumnHeader()}
              {showAddColumn && renderAddColumnHeader()}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={row.id || rowIndex}>
                {showCheckbox && renderCheckboxColumnData(row, rowIndex)}

                {stickyColumns.map((column, index) => (
                  <td
                    key={`${row.id || rowIndex}-${column.key}`}
                    style={{
                      left: `${getStickyLeftOffset(index)}px`,
                      width: column.width || "auto",
                      minWidth: column.width || "auto",
                      maxWidth: column.width || "none",
                    }}
                    className={`sticky-body-column ${
                      rowIndex % 2 === 0 ? "row-even-bg" : "row-odd-bg"
                    } ${column.className || ""}`}
                  >
                    {renderCellContent(column, row)}
                  </td>
                ))}

                {draggableColumns.map(column => (
                  <td
                    key={`${row.id || rowIndex}-${column.key}`}
                    style={{
                      width: column.width || "auto",
                      minWidth: column.width || "auto",
                      maxWidth: column.width || "none",
                    }}
                    className={column.className || ""}
                  >
                    {renderCellContent(column, row)}
                  </td>
                ))}
                {menuColumn && renderMenuColumnData(row, rowIndex)}
                {showAddColumn && renderAddColumnData()}
              </tr>
            ))}
          </tbody>
        </table>
      </DndContext>
    </div>
  )
}

export default BasicTable
